import React, { useState } from 'react';
import { useContract } from '../../context/ContractContext';
import jsPDF from 'jspdf';

const ContractPreview = () => {
  const { contractData } = useContract();
  const [isGenerating, setIsGenerating] = useState(false);

  const getContractTypeName = () => {
    const types = {
      'locacao': 'LOCAÇÃO',
      'compra-venda': 'COMPRA E VENDA',
      'administracao': 'ADMINISTRAÇÃO PREDIAL'
    };
    return types[contractData.type] || 'CONTRATO IMOBILIÁRIO';
  };

  const getPropertyTypeName = () => {
    const types = {
      'apartamento': 'apartamento',
      'casa': 'casa',
      'comercial': 'imóvel comercial',
      'terreno': 'terreno',
      'sitio': 'sítio/fazenda'
    };
    return types[contractData.property?.type] || 'imóvel';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDocument = (doc, type) => {
    if (!doc) return '[documento]';
    if (type === 'cpf') {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (type === 'cnpj') {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const getSelectedClauses = () => {
    const standardClauses = contractData.clauses?.standardClauses || [];
    const customClauses = contractData.clauses?.customClauses || [];
    return {
      standard: standardClauses.length,
      custom: customClauses.length,
      total: standardClauses.length + customClauses.length
    };
  };

  const getTotalValue = () => {
    const rent = parseFloat(contractData.financial?.rentValue) || 0;
    const fees = (contractData.financial?.fees || []).reduce((sum, fee) => sum + (parseFloat(fee.value) || 0), 0);
    return rent + fees;
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      let yPos = margin;

      const checkPageBreak = (space = 15) => {
        if (yPos + space > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      const addText = (text, fontSize = 11, style = 'normal', align = 'left', indent = 0) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', style);
        
        const maxWidth = pageWidth - (2 * margin) - indent;
        const lines = doc.splitTextToSize(text, maxWidth);
        
        lines.forEach((line, index) => {
          if (index > 0) checkPageBreak(6);
          
          let x = margin + indent;
          if (align === 'center') {
            x = pageWidth / 2;
          } else if (align === 'right') {
            x = pageWidth - margin;
          }
          
          doc.text(line, x, yPos, { align });
          yPos += 6;
        });
      };

      // CABEÇALHO
      doc.setFillColor(255, 184, 0);
      doc.circle(margin + 5, yPos + 3, 5, 'F');
      
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('mara', margin + 12, yPos + 5);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('L A B S', margin + 12, yPos + 9);
      
      doc.setFontSize(9);
      doc.text('Sistema de Gestão de Contratos', pageWidth - margin, yPos, { align: 'right' });
      doc.text('www.maralabs.com.br', pageWidth - margin, yPos + 5, { align: 'right' });
      doc.text('contato@maralabs.com.br', pageWidth - margin, yPos + 10, { align: 'right' });
      
      yPos += 25;

      // TÍTULO
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('CONTRATO DE ' + getContractTypeName(), pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      doc.setFillColor(255, 184, 0);
      doc.rect((pageWidth / 2) - 15, yPos, 30, 2, 'F');
      yPos += 15;

      // PARTES
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Partes:', margin, yPos);
      yPos += 8;

      const ownerDoc = contractData.parties?.owner?.type === 'pf' 
        ? formatDocument(contractData.parties?.owner?.cpf, 'cpf')
        : formatDocument(contractData.parties?.owner?.cnpj, 'cnpj');

      const tenantDoc = contractData.parties?.tenant?.type === 'pf' 
        ? formatDocument(contractData.parties?.tenant?.cpf, 'cpf')
        : formatDocument(contractData.parties?.tenant?.cnpj, 'cnpj');

      const partiesText = 'De um lado, ' + (contractData.parties?.owner?.name || contractData.parties?.owner?.companyName || '[Nome do Proprietário]') + ', ' + (contractData.parties?.owner?.type === 'pf' ? 'portador(a) do CPF' : 'inscrito(a) no CNPJ') + ' nº ' + ownerDoc + ', residente e domiciliado(a) à ' + (contractData.parties?.owner?.address || '[Endereço]') + ', ' + (contractData.parties?.owner?.city || '[Cidade]') + ' - ' + (contractData.parties?.owner?.state || '[Estado]') + ', doravante denominada simplesmente "' + (contractData.type === 'compra-venda' ? 'VENDEDOR(A)' : 'LOCADOR(A)') + '", e de outro lado, ' + (contractData.parties?.tenant?.name || contractData.parties?.tenant?.companyName || '[Nome do Locatário]') + ', ' + (contractData.parties?.tenant?.type === 'pf' ? 'portador(a) do CPF' : 'inscrito(a) no CNPJ') + ' nº ' + tenantDoc + ', residente e domiciliado(a) à ' + (contractData.parties?.tenant?.address || '[Endereço]') + ', ' + (contractData.parties?.tenant?.city || '[Cidade]') + ' - ' + (contractData.parties?.tenant?.state || '[Estado]') + ', doravante denominado simplesmente "' + (contractData.type === 'compra-venda' ? 'COMPRADOR(A)' : 'LOCATÁRIO(A)') + '".';

      addText(partiesText, 11, 'normal', 'left', 5);
      yPos += 8;

      // OBJETO
      checkPageBreak(25);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Objeto:', margin, yPos);
      yPos += 8;

      let objectText = 'O presente contrato tem por objeto ' + (contractData.property?.type === 'apartamento' ? 'um apartamento' : contractData.property?.type === 'casa' ? 'uma casa' : 'um ' + getPropertyTypeName()) + ' localizado no seguinte endereço: ' + (contractData.property?.address || '[Endereço do Imóvel]') + ', ' + (contractData.property?.city || '[Cidade]') + ' - ' + (contractData.property?.state || '[Estado]') + ', CEP: ' + (contractData.property?.zipCode || '[CEP]') + '.';

      if (contractData.property?.area) {
        objectText += ' O imóvel possui área total de ' + contractData.property.area + 'm².';
      }

      if (contractData.property?.bedrooms || contractData.property?.bathrooms || contractData.property?.parking) {
        const characteristics = [];
        if (contractData.property?.bedrooms) characteristics.push(contractData.property.bedrooms + ' dormitório' + (contractData.property.bedrooms > 1 ? 's' : ''));
        if (contractData.property?.bathrooms) characteristics.push(contractData.property.bathrooms + ' banheiro' + (contractData.property.bathrooms > 1 ? 's' : ''));
        if (contractData.property?.parking) characteristics.push(contractData.property.parking + ' vaga' + (contractData.property.parking > 1 ? 's' : '') + ' de garagem');
        objectText += ' Características do imóvel: ' + characteristics.join(', ') + '.';
      }

      addText(objectText, 11, 'normal', 'left', 5);
      yPos += 8;

      // CLÁUSULAS
      checkPageBreak(25);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Cláusulas:', margin, yPos);
      yPos += 10;

      // Cláusula 1
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('1. Da Natureza e Objeto do Contrato:', margin, yPos);
      yPos += 6;
      addText('O presente contrato tem por objeto a ' + (contractData.type === 'compra-venda' ? 'compra e venda' : 'locação') + ' do imóvel acima descrito, que se encontra em perfeitas condições de habitabilidade e uso.', 11, 'normal', 'left', 5);
      yPos += 5;

      // Cláusula 2
      checkPageBreak(20);
      doc.setFont('helvetica', 'bold');
      doc.text('2. Da Finalidade:', margin, yPos);
      yPos += 6;
      addText(contractData.type === 'compra-venda' 
        ? 'O imóvel destina-se ao uso que melhor convier ao(à) comprador(a), respeitadas as limitações legais e regulamentares vigentes.'
        : 'O imóvel destina-se exclusivamente ao uso residencial do(a) locatário(a) e sua família, sendo vedada qualquer atividade comercial ou profissional sem prévia autorização do(a) locador(a).', 11, 'normal', 'left', 5);
      yPos += 5;

      // Cláusula 3
      checkPageBreak(25);
      doc.setFont('helvetica', 'bold');
      doc.text('3. ' + (contractData.type === 'compra-venda' ? 'Do Preço e Forma de Pagamento' : 'Do Valor do Aluguel e Prazo') + ':', margin, yPos);
      yPos += 6;
      
      let clause3Text = '';
      if (contractData.type === 'compra-venda') {
        clause3Text = 'O preço total da venda é de ' + formatCurrency(contractData.financial?.rentValue) + '.';
        if (contractData.financial?.deposit > 0) {
          clause3Text += ' O valor de entrada é de ' + formatCurrency(contractData.financial.deposit) + ', e o restante será pago conforme acordado entre as partes.';
        } else {
          clause3Text += ' A forma de pagamento será conforme acordado entre as partes.';
        }
      } else {
        clause3Text = 'O valor do aluguel mensal é de ' + formatCurrency(contractData.financial?.rentValue) + ', a ser pago impreterivelmente até o dia ' + (contractData.financial?.dueDate || 10) + ' de cada mês.';
        if (contractData.financial?.deposit > 0) {
          clause3Text += ' O valor da caução/depósito é de ' + formatCurrency(contractData.financial.deposit) + '.';
        }
        if (contractData.financial?.contractDuration) {
          clause3Text += ' O presente contrato vigorará pelo prazo de ' + contractData.financial.contractDuration + ' meses.';
        }
      }
      addText(clause3Text, 11, 'normal', 'left', 5);
      yPos += 5;

      // Taxas adicionais
      if (contractData.financial?.fees && contractData.financial.fees.length > 0) {
        checkPageBreak(20);
        doc.setFont('helvetica', 'bold');
        doc.text('4. Das Taxas e Encargos Adicionais:', margin, yPos);
        yPos += 6;
        
        let feesText = 'Além do valor principal, as partes acordam os seguintes encargos adicionais: ';
        contractData.financial.fees.forEach((fee, index) => {
          feesText += fee.name + ': ' + formatCurrency(fee.value) + (fee.description ? ' - ' + fee.description : '') + (index < contractData.financial.fees.length - 1 ? '; ' : '.');
        });
        addText(feesText, 11, 'normal', 'left', 5);
        yPos += 5;
      }

      // Cláusula responsabilidades
      const clauseNum = contractData.financial?.fees && contractData.financial.fees.length > 0 ? 5 : 4;
      checkPageBreak(20);
      doc.setFont('helvetica', 'bold');
      doc.text(clauseNum + '. Das Responsabilidades do ' + (contractData.type === 'compra-venda' ? 'Vendedor' : 'Locador') + ':', margin, yPos);
      yPos += 6;
      addText(contractData.type === 'compra-venda'
        ? 'O(A) vendedor(a) obriga-se a entregar o imóvel livre e desembaraçado de quaisquer ônus, gravames, dívidas ou ações judiciais. O(A) vendedor(a) garante que o imóvel é de sua propriedade plena e exclusiva.'
        : 'O(A) locador(a) obriga-se a garantir ao(à) locatário(a) o uso pacífico do imóvel durante toda a vigência do contrato, responsabilizando-se pelas despesas extraordinárias de conservação e reparos estruturais necessários.', 11, 'normal', 'left', 5);
      yPos += 5;

      checkPageBreak(20);
      doc.setFont('helvetica', 'bold');
      doc.text((clauseNum + 1) + '. Das Responsabilidades do ' + (contractData.type === 'compra-venda' ? 'Comprador' : 'Locatário') + ':', margin, yPos);
      yPos += 6;
      addText(contractData.type === 'compra-venda'
        ? 'O(A) comprador(a) responsabiliza-se pelo pagamento de todos os impostos, taxas e emolumentos incidentes sobre a transferência do imóvel, bem como pelos tributos e encargos a partir da data da escritura pública.'
        : 'O(A) locatário(a) obriga-se a manter o imóvel em perfeito estado de conservação, responsabilizando-se por danos causados por uso inadequado. Obriga-se também ao pagamento pontual do aluguel e das despesas ordinárias de consumo.', 11, 'normal', 'left', 5);
      yPos += 5;

      checkPageBreak(20);
      doc.setFont('helvetica', 'bold');
      doc.text((clauseNum + 2) + '. Das Disposições Gerais:', margin, yPos);
      yPos += 6;
      addText('Este contrato é regido pelas leis vigentes da República Federativa do Brasil, sendo eleito o foro da comarca de ' + (contractData.property?.city || '[Cidade]') + ', Estado de ' + (contractData.property?.state || '[Estado]') + ', para dirimir quaisquer controvérsias dele decorrentes, com expressa renúncia de qualquer outro, por mais privilegiado que seja.', 11, 'normal', 'left', 5);
      yPos += 10;

      // Texto final
      checkPageBreak(15);
      addText('E por estarem assim justas e contratadas, as partes firmam o presente instrumento em duas vias de igual teor e forma, na presença das testemunhas abaixo identificadas, para que produza todos os efeitos legais.', 11, 'italic', 'left');
      yPos += 15;

      // ASSINATURAS
      checkPageBreak(60);
      
      const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
      const today = new Date();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text((contractData.property?.city || '[Cidade]') + ', ' + today.getDate() + ' de ' + months[today.getMonth()] + ' de ' + today.getFullYear(), pageWidth / 2, yPos, { align: 'center' });
      yPos += 25;

      const sigWidth = 70;
      const sig1X = margin + 10;
      const sig2X = pageWidth - margin - sigWidth - 10;

      doc.line(sig1X, yPos, sig1X + sigWidth, yPos);
      doc.setFontSize(10);
      doc.text(contractData.parties?.owner?.name || contractData.parties?.owner?.companyName || '[Nome]', sig1X + (sigWidth / 2), yPos + 5, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.text(contractData.type === 'compra-venda' ? 'VENDEDOR(A)' : 'LOCADOR(A)', sig1X + (sigWidth / 2), yPos + 10, { align: 'center' });

      doc.line(sig2X, yPos, sig2X + sigWidth, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(contractData.parties?.tenant?.name || contractData.parties?.tenant?.companyName || '[Nome]', sig2X + (sigWidth / 2), yPos + 5, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.text(contractData.type === 'compra-venda' ? 'COMPRADOR(A)' : 'LOCATÁRIO(A)', sig2X + (sigWidth / 2), yPos + 10, { align: 'center' });

      yPos += 20;

      // TESTEMUNHAS
      checkPageBreak(50);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Testemunhas:', margin, yPos);
      yPos += 15;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('1.', margin, yPos);
      doc.line(margin + 5, yPos, margin + 80, yPos);
      doc.text('1.ª TESTEMUNHA', margin + 5, yPos + 5);
      doc.setFont('helvetica', 'normal');
      doc.text('Nome: _________________________________', margin + 5, yPos + 12);
      doc.text('CPF: __________________________________', margin + 5, yPos + 18);

      const test2X = pageWidth - margin - 80;
      doc.setFont('helvetica', 'bold');
      doc.text('2.', test2X - 5, yPos);
      doc.line(test2X, yPos, pageWidth - margin, yPos);
      doc.text('2.ª TESTEMUNHA', test2X, yPos + 5);
      doc.setFont('helvetica', 'normal');
      doc.text('Nome: _________________________________', test2X, yPos + 12);
      doc.text('CPF: __________________________________', test2X, yPos + 18);

      yPos += 30;

      // RODAPÉ
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = pageHeight - 30;
      } else {
        yPos = pageHeight - 30;
      }

      doc.setFillColor(255, 184, 0);
      doc.circle(margin + 3, yPos + 2, 3, 'F');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('mara', margin + 8, yPos + 3);
      doc.setFontSize(6);
      doc.text('L A B S', margin + 8, yPos + 6);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Sistema de Gestão de Contratos', pageWidth - margin, yPos, { align: 'right' });
      doc.text('www.maralabs.com.br', pageWidth - margin, yPos + 4, { align: 'right' });
      doc.text('contato@maralabs.com.br', pageWidth - margin, yPos + 8, { align: 'right' });

      const fileName = 'contrato-' + contractData.type + '-' + new Date().toISOString().split('T')[0] + '.pdf';
      doc.save(fileName);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const clauses = getSelectedClauses();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-dark-900 mb-2">
          Revisão Final do Contrato
        </h3>
        <p className="text-neutral-600 mb-6">
          Revise todas as informações antes de gerar o contrato
        </p>

        <div className="bg-white rounded-lg shadow-md border p-6 mb-6 bg-primary-50 border-primary-200">
          <h4 className="text-lg font-semibold text-primary-800 mb-4">
            Resumo Geral
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-primary-600">Tipo de Contrato:</span>
              <p className="font-semibold text-primary-800">{getContractTypeName()}</p>
            </div>
            <div>
              <span className="text-sm text-primary-600">Valor Principal:</span>
              <p className="font-semibold text-primary-800">{formatCurrency(contractData.financial?.rentValue)}</p>
            </div>
            <div>
              <span className="text-sm text-primary-600">Valor Total:</span>
              <p className="font-semibold text-primary-800">{formatCurrency(getTotalValue())}</p>
            </div>
            <div>
              <span className="text-sm text-primary-600">Cláusulas:</span>
              <p className="font-semibold text-primary-800">{clauses.total} selecionadas</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border p-6 mb-6">
          <h4 className="text-lg font-semibold text-dark-900 mb-4">
            Dados do Imóvel
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600">Tipo:</span>
              <span className="font-medium">{getPropertyTypeName()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">Endereço:</span>
              <span className="font-medium text-right">{contractData.property?.address || 'Não informado'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">Cidade/Estado:</span>
              <span className="font-medium">{contractData.property?.city || 'Não informado'}/{contractData.property?.state || 'Não informado'}</span>
            </div>
            {contractData.property?.area && (
              <div className="flex justify-between">
                <span className="text-neutral-600">Área:</span>
                <span className="font-medium">{contractData.property.area}m²</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md border p-6">
            <h4 className="text-lg font-semibold text-dark-900 mb-4">
              {contractData.type === 'compra-venda' ? 'Vendedor' : 'Proprietário'}
            </h4>
            {contractData.parties?.owner ? (
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-neutral-600">Nome:</span>
                  <p className="font-medium">{contractData.parties.owner.name || contractData.parties.owner.companyName || 'Não informado'}</p>
                </div>
                <div>
                  <span className="text-sm text-neutral-600">Documento:</span>
                  <p className="font-medium">
                    {contractData.parties.owner.type === 'pf' 
                      ? formatDocument(contractData.parties.owner.cpf, 'cpf')
                      : formatDocument(contractData.parties.owner.cnpj, 'cnpj')
                    }
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-neutral-500 italic">Dados não preenchidos</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md border p-6">
            <h4 className="text-lg font-semibold text-dark-900 mb-4">
              {contractData.type === 'compra-venda' ? 'Comprador' : 'Locatário'}
            </h4>
            {contractData.parties?.tenant ? (
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-neutral-600">Nome:</span>
                  <p className="font-medium">{contractData.parties.tenant.name || contractData.parties.tenant.companyName || 'Não informado'}</p>
                </div>
                <div>
                  <span className="text-sm text-neutral-600">Documento:</span>
                  <p className="font-medium">
                    {contractData.parties.tenant.type === 'pf' 
                      ? formatDocument(contractData.parties.tenant.cpf, 'cpf')
                      : formatDocument(contractData.parties.tenant.cnpj, 'cnpj')
                    }
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-neutral-500 italic">Dados não preenchidos</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border p-6 bg-success-50 border-success-200">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-success-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-success-800 mb-2">
                Contrato Pronto para Geração
              </h4>
              <p className="text-success-700 mb-6">
                Todas as informações necessárias foram preenchidas. O contrato está pronto para ser gerado em PDF.
              </p>

              <button
                onClick={generatePDF}
                disabled={isGenerating}
                className={
                  'flex items-center justify-center px-8 py-4 rounded-lg font-medium transition-all text-lg ' +
                  (isGenerating 
                    ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed' 
                    : 'bg-success-600 hover:bg-success-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105')
                }
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
                    Gerando PDF...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Gerar Contrato em PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border p-6 bg-yellow-50 border-yellow-200">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.098 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                Importante
              </h4>
              <div className="text-yellow-700 space-y-2 text-sm">
                <p>• Revise cuidadosamente todas as informações antes de gerar o contrato</p>
                <p>• O PDF será baixado automaticamente após a geração</p>
                <p>• Recomenda-se a revisão por um advogado antes da assinatura</p>
                <p>• Mantenha cópias do contrato assinado para todos os envolvidos</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractPreview;